import { useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { api } from '../../services/api';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { ErrorNotice } from '../ui/States';
import { dateShort } from '../../lib/format';
import { EditionSummary, EventRef, FAMILY_EVENT_KEYS, apiError } from '../../lib/forms';

const inputClass = 'mt-1 w-full border border-line px-3 py-2 text-table text-ink bg-white';

export default function AcceptFormModal({
  edition,
  families,
  onClose,
  onDone,
}: {
  edition: EditionSummary;
  families: { key: string; name: string }[];
  onClose: () => void;
  onDone: () => void;
}) {
  const suggested = edition.suggestion?.familyKey ?? '';
  const [familyKey, setFamilyKey] = useState(suggested);
  const [label, setLabel] = useState(edition.label ?? edition.title ?? '');
  const [year, setYear] = useState(String(edition.year ?? edition.suggestion?.year ?? ''));
  const [cohort, setCohort] = useState(edition.cohort ?? '');
  const [eventChoice, setEventChoice] = useState<string>(edition.event?.id ?? '');
  const [newEvent, setNewEvent] = useState({ title: '', startsAt: '', location: '', mode: '' });

  const needsEvent = FAMILY_EVENT_KEYS.has(familyKey);
  const events = useQuery<EventRef[]>('form-events', async () => (await api.get('/forms/events')).data.data, {
    enabled: needsEvent,
  });

  const accept = useMutation(
    async () => {
      const body: Record<string, unknown> = {
        familyKey,
        label: label.trim() || null,
        year: year ? Number(year) : null,
        cohort: cohort.trim() || null,
      };
      if (needsEvent && eventChoice === 'new' && newEvent.title.trim()) {
        body.event = { ...newEvent, mode: newEvent.mode || null, startsAt: newEvent.startsAt || null };
      } else if (needsEvent && eventChoice && eventChoice !== 'new') {
        body.eventId = eventChoice;
      }
      return (await api.post(`/forms/editions/${edition.id}/accept`, body)).data.data;
    },
    { onSuccess: onDone },
  );

  return (
    <Modal
      title="Accept form"
      onClose={onClose}
      footer={
        <>
          <Button variant="primary" disabled={!familyKey || accept.isLoading} onClick={() => accept.mutate()}>
            {accept.isLoading ? 'Processing…' : 'Accept and process entries'}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </>
      }
    >
      <p className="text-table">
        <strong>{edition.title ?? `Form ${edition.formId}`}</strong> (form {edition.formId}) ·{' '}
        {edition.entries || edition.upstreamEntryCount || 0} entries. Everything it has collected is processed into the
        family you choose; only Registration ever creates members.
      </p>
      {edition.suggestion?.copiedFrom ? (
        <p className="text-small text-ink-2 mt-2">
          Its questions match {edition.suggestion.copiedFrom.label} (form {edition.suggestion.copiedFrom.formId}), so
          that edition’s mapping carries over.
        </p>
      ) : null}
      <div className="grid grid-cols-2 gap-3 mt-4">
        <label className="text-small text-ink-2 col-span-2">
          Family
          <select value={familyKey} onChange={(e) => setFamilyKey(e.target.value)} className={inputClass}>
            <option value="">Choose…</option>
            {families.map((f) => (
              <option key={f.key} value={f.key}>
                {f.name}
                {f.key === suggested ? ' (suggested)' : ''}
              </option>
            ))}
          </select>
        </label>
        <label className="text-small text-ink-2 col-span-2">
          Label
          <input value={label} onChange={(e) => setLabel(e.target.value)} className={inputClass} />
        </label>
        <label className="text-small text-ink-2">
          Year
          <input
            value={year}
            onChange={(e) => setYear(e.target.value.replace(/\D/g, '').slice(0, 4))}
            className={inputClass}
          />
        </label>
        <label className="text-small text-ink-2">
          Cohort / intake
          <input value={cohort} onChange={(e) => setCohort(e.target.value)} className={inputClass} />
        </label>
        {needsEvent ? (
          <label className="text-small text-ink-2 col-span-2">
            Event
            <select value={eventChoice} onChange={(e) => setEventChoice(e.target.value)} className={inputClass}>
              <option value="">No single event (e.g. an all-session survey)</option>
              <option value="new">New event…</option>
              {(events.data ?? []).map((e) => (
                <option key={e.id} value={e.id}>
                  {e.title}
                  {e.startsAt ? ` · ${dateShort(e.startsAt)}` : ''}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {needsEvent && eventChoice === 'new' ? (
          <>
            <label className="text-small text-ink-2 col-span-2">
              Event title
              <input
                value={newEvent.title}
                onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                className={inputClass}
              />
            </label>
            <label className="text-small text-ink-2">
              Date
              <input
                type="date"
                value={newEvent.startsAt}
                onChange={(e) => setNewEvent({ ...newEvent, startsAt: e.target.value })}
                className={inputClass}
              />
            </label>
            <label className="text-small text-ink-2">
              Format
              <select
                value={newEvent.mode}
                onChange={(e) => setNewEvent({ ...newEvent, mode: e.target.value })}
                className={inputClass}
              >
                <option value="">Unknown</option>
                <option value="in_person">In person</option>
                <option value="online">Online</option>
                <option value="hybrid">Hybrid</option>
              </select>
            </label>
            <label className="text-small text-ink-2 col-span-2">
              Location
              <input
                value={newEvent.location}
                onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
                className={inputClass}
              />
            </label>
          </>
        ) : null}
      </div>
      {accept.isError ? (
        <div className="mt-4">
          <ErrorNotice>{apiError(accept.error, 'The form could not be accepted.')}</ErrorNotice>
        </div>
      ) : null}
    </Modal>
  );
}
